import { generateSlug } from '../WorkProjectsList/WorkProjectItem/WorkProject';
import { API_URL } from '../../config/constants';

class SitemapDataCache {
  constructor(maxSize = 50, defaultTTL = 5 * 60 * 1000) {
    this.cache = new Map();
    this.maxSize = maxSize;
    this.defaultTTL = defaultTTL;
  }

  set(key, data, ttl = this.defaultTTL) {
    if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }

    this.cache.set(key, {
      data,
      expiry: Date.now() + ttl,
      timestamp: Date.now()
    });
  }

  get(key) {
    const item = this.cache.get(key);

    if (!item) return null;

    if (Date.now() > item.expiry) {
      this.cache.delete(key);
      return null;
    }

    return item.data;
  }

  has(key) {
    const item = this.cache.get(key);
    if (!item) return false;

    if (Date.now() > item.expiry) {
      this.cache.delete(key);
      return false;
    }

    return true;
  }

  clear() {
    this.cache.clear();
  }

  getStats() {
    const now = Date.now();
    const validEntries = Array.from(this.cache.entries()).filter(([_, item]) => now <= item.expiry);

    return {
      totalSize: this.cache.size,
      validEntries: validEntries.length,
      oldestEntry: Math.min(...Array.from(this.cache.values()).map(item => item.timestamp))
    };
  }
}

const sitemapCache = new SitemapDataCache();

const loadWithRetry = async (url, retries = 3, delay = 1000) => {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.warn(`Attempt ${attempt} failed for ${url}:`, error.message);

      if (attempt === retries) {
        throw error;
      }

      await new Promise(resolve => setTimeout(resolve, delay * attempt));
    }
  }
};

const loadCachedData = async (url, cacheKey = url, ttl = 5 * 60 * 1000) => {
  if (sitemapCache.has(cacheKey)) {
    return sitemapCache.get(cacheKey);
  }

  try {
    const data = await loadWithRetry(url);
    sitemapCache.set(cacheKey, data, ttl);
    return data;
  } catch (error) {
    console.error(`Failed to load data from ${url}:`, error);
    throw error;
  }
};

export const getSitemapData = async (language = 'en') => {
  const cacheKey = `sitemap-${language}`;

  if (sitemapCache.has(cacheKey)) {
    return sitemapCache.get(cacheKey);
  }

  try {
    const [sitemapData, articleData, workData] = await Promise.all([
      loadCachedData(`${API_URL}/content/sitemap`, 'sitemap-raw'),
      loadCachedData(`${API_URL}/content/articles`, 'articles-raw'),
      loadCachedData(`${API_URL}/content/work`, 'work-raw')
    ]);

    const langData = sitemapData.translations[language] || sitemapData.translations.en;

    const uniqueCategories = [
      ...new Map(
        (articleData.ArticleData || [])
          .filter(article => article?.webLink?.category?.slug)
          .map(article => [
            article.webLink.category.slug,
            {
              name: article.webLink.category.categoryName,
              slug: article.webLink.category.slug
            }
          ])
      ).values()
    ];

    const blogCategoryLinks = uniqueCategories.map(cat => ({
      title: typeof cat.name === 'object' ? cat.name[language] || cat.name.en : cat.name,
      loc: `/blog/${cat.slug}`,
      lastmod: '',
      changefreq: 'monthly',
      priority: 0.6
    }));

    const workUrls = (workData.WorkData || [])
      .filter(project => project?.mainTitle)
      .map(project => ({
        title: typeof project.mainTitle === 'object'
          ? project.mainTitle[language] || project.mainTitle.en
          : project.mainTitle,
        loc: `/work/${generateSlug(project.mainTitle)}`,
        lastmod: project.updatedAt || '',
        changefreq: 'monthly',
        priority: 0.7
      }));

    const staticUrlsBase = sitemapData.staticUrls || [];

    const staticUrlsWithImpressum = [...staticUrlsBase];
    if (language === 'de') {
      staticUrlsWithImpressum.push({
        loc: "/impressum",
        lastmod: "2025-03-26",
        changefreq: "yearly",
        priority: 0.5
      });
    }

    const staticUrls = (sitemapData.staticUrls || [])
      .filter(url => {
        if (url.loc === '/impressum' && language !== 'de') {
          return false;
        }
        return true;
      })
      .map(url => ({
        title: langData.urlTitles?.[url.loc] || url.loc,
        loc: url.loc,
        lastmod: url.lastmod || '',
        changefreq: url.changefreq || 'monthly',
        priority: url.priority || 0.8
      }));

    const allUrls = [...staticUrls, ...workUrls, ...blogCategoryLinks];

    const urlMap = new Map();
    allUrls.forEach(url => {
      if (!urlMap.has(url.loc) || (url.priority > (urlMap.get(url.loc)?.priority || 0))) {
        urlMap.set(url.loc, url);
      }
    });

    const uniqueUrls = Array.from(urlMap.values());

    const result = {
      sitemapTitle: langData.sitemapTitle || 'Sitemap',
      sitemapHomeTitle: langData.sitemapHomeTitle || 'Home',
      sitemapMarketingTitle: langData.sitemapMarketingTitle || 'Marketing',
      sitemapServicesTitle: langData.sitemapServicesTitle || 'Services',
      sitemapWorkTitle: langData.sitemapWorkTitle || 'Work',
      sitemapInsightsTitle: langData.sitemapInsightsTitle || 'Insights',
      urls: uniqueUrls,
      generatedAt: new Date().toISOString(),
      language: language
    };

    sitemapCache.set(cacheKey, result, 10 * 60 * 1000);

    return result;
  } catch (error) {
    console.error('Error in getSitemapData:', error);
    throw error;
  }
};

export const clearSitemapCache = (specificKey = null) => {
  if (specificKey) {
    sitemapCache.delete(specificKey);
  } else {
    sitemapCache.clear();
  }
};

export const getSitemapCacheStats = () => {
  return sitemapCache.getStats();
};

export const prefetchSitemapData = (language = 'en') => {
  getSitemapData(language).catch(error => {
    console.warn('Prefetch failed:', error);
  });
};

export const validateSitemapData = (data) => {
  const errors = [];

  if (!data?.urls || !Array.isArray(data.urls)) {
    errors.push('Invalid urls array');
    return errors;
  }

  data.urls.forEach((url, index) => {
    if (!url.loc) {
      errors.push(`URL at index ${index} missing 'loc' property`);
    }
    if (!url.title) {
      errors.push(`URL at index ${index} missing 'title' property`);
    }
    if (typeof url.priority !== 'number') {
      errors.push(`URL at index ${index} has invalid 'priority'`);
    }
  });

  return errors;
};